import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import STEMForgeLogo from './STEMForgeLogo';

export default function Navbar() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();

    if (href.startsWith('#')) {
      const scrollToElement = (selector: string) => {
        const element = document.querySelector(selector);
        if (element) {
          const offset = 80; // Account for fixed navbar
          const top = element.getBoundingClientRect().top + window.scrollY - offset;
          window.scrollTo({ top, behavior: 'smooth' });
        }
      };

      // If we're not on the home page, navigate there first
      if (window.location.pathname !== '/' && window.location.pathname !== __BASE_PATH__ && window.location.pathname !== `${__BASE_PATH__}/`) {
        navigate('/');
        // Wait for navigation to complete, then scroll
        setTimeout(() => {
          scrollToElement(href);
        }, 100);
      } else {
        // Already on home page, just scroll
        scrollToElement(href);
      }
    }
  };

  return (
    <nav
      className="fixed top-0 left-0 right-0 z-[1000] bg-white shadow-lg transition-all duration-300"
      style={{ position: 'fixed', top: 0, width: '100%' }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20 py-4">
          {/* Logo */}
          <Link to="/" className="flex items-center cursor-pointer flex-shrink-0">
            <STEMForgeLogo height={40} gradientId="nav-logo-grad" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-4 lg:space-x-6">
            <a
              href="/#about"
              onClick={(e) => handleNavClick(e, '#about')}
              className="text-sm font-medium text-gray-700 hover:text-green-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              About
            </a>
            <Link
              to="/solutions"
              className="text-sm font-medium text-gray-700 hover:text-green-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              Solutions
            </Link>
            <Link
              to="/academic-integration"
              className="text-sm font-medium text-gray-700 hover:text-green-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              Academic Integration
            </Link>
            <Link
              to="/testimonials"
              className="text-sm font-medium text-gray-700 hover:text-green-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              Testimonials
            </Link>
            <a
              href="/#contact"
              onClick={(e) => {
                e.preventDefault();
                const contactSection = document.getElementById('contact');
                if (contactSection) {
                  const offset = 80; // Account for fixed navbar
                  const top = contactSection.getBoundingClientRect().top + window.scrollY - offset;
                  window.scrollTo({ top, behavior: 'smooth' });
                } else {
                  window.location.href = '/#contact';
                }
              }}
              className="inline-flex items-center gap-1.5 lg:gap-2 px-4 lg:px-5 py-2 bg-gradient-to-r from-green-600 to-teal-600 text-white text-xs lg:text-sm font-semibold rounded-lg hover:from-green-700 hover:to-teal-700 transition-all shadow-lg whitespace-nowrap cursor-pointer"
            >
              <i className="ri-calendar-check-line"></i>
              <span className="hidden lg:inline">Schedule Consultation</span>
              <span className="lg:hidden">Consult</span>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2 rounded-lg transition-colors cursor-pointer text-gray-700 hover:bg-gray-100"
          >
            <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-200 shadow-xl">
          <div className="px-6 py-4 space-y-3">
            <a
              href="/#about"
              onClick={(e) => {
                e.preventDefault();
                setIsMobileMenuOpen(false);
                const aboutSection = document.getElementById('about');
                if (aboutSection) {
                  const offset = 80; // Account for fixed navbar
                  const top = aboutSection.getBoundingClientRect().top + window.scrollY - offset;
                  window.scrollTo({ top, behavior: 'smooth' });
                } else {
                  window.location.href = '/#about';
                }
              }}
              className="block py-2 text-gray-700 hover:text-green-600 font-medium cursor-pointer"
            >
              About
            </a>
            <Link
              to="/solutions"
              className="block py-2 text-gray-700 hover:text-green-600 font-medium cursor-pointer"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Solutions
            </Link>
            <Link
              to="/academic-integration"
              className="block py-2 text-gray-700 hover:text-green-600 font-medium cursor-pointer"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Academic Integration
            </Link>
            <Link
              to="/testimonials"
              className="block py-2 text-gray-700 hover:text-green-600 font-medium cursor-pointer"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Testimonials
            </Link>
            <a
              href="/#contact"
              onClick={(e) => {
                e.preventDefault();
                setIsMobileMenuOpen(false);
                const contactSection = document.getElementById('contact');
                if (contactSection) {
                  const offset = 80; // Account for fixed navbar
                  const top = contactSection.getBoundingClientRect().top + window.scrollY - offset;
                  window.scrollTo({ top, behavior: 'smooth' });
                } else {
                  window.location.href = '/#contact';
                }
              }}
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-green-600 to-teal-600 text-white text-sm font-semibold rounded-lg hover:from-green-700 hover:to-teal-700 transition-all shadow-lg whitespace-nowrap cursor-pointer w-full justify-center"
            >
              <i className="ri-calendar-check-line"></i>
              Schedule Consultation
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}